import {Injectable, NgZone} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {find} from 'lodash';
import {map, skip, switchMap, take, tap, delay, filter} from 'rxjs/operators';
import {Observable} from 'rxjs';

import {NavigationService} from '../../core/navigation/navigation.service';
import {AIDVGenerationStatus} from '../models/generation-status.interface';
import {DiagramJsonHandlerService} from '../../diagram-data/diagram-json-handler.service';
import {DrilldownService} from '../../diagram-interaction/services/drilldown.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../editor-interface/shared/models/diagram-version.constant';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {MxCell} from '../../editor-interface/shared/models/mx-cell.interface';
import {environment} from '../../../environments/environment';

@Injectable({providedIn: 'root'})
export class GenerationHandlerService {
  apiUrl: string;
  constructor(
    private navSvc: NavigationService,
    public diagramJsonSvc: DiagramJsonHandlerService,
    private editorRegistrySvc: EditorRegistryService,
    private drilldownSvc: DrilldownService,
    private ngZone: NgZone,
    private http: HttpClient
  ) {
    this.apiUrl = environment.API_ENDPOINT;
  }

  public retryDrilldown(status: AIDVGenerationStatus): void {
    const draft = DIAGRAM_VERSION_STATES_ENUM.DRAFT;
    let oldDV;
    let newDV;

    this.diagramJsonSvc.diagramJson$
      .pipe(
        take(1),
        tap(diagramJson => (oldDV = diagramJson)),
        switchMap(({parentDiagramId}) => this.navSvc.navigate(parentDiagramId, draft, true)),
        switchMap(() => this.diagramJsonSvc.diagramJson$.pipe(skip(1), take(1))),
        tap(dv => (newDV = dv)),
        delay(0),
        map(() => this.getCell(oldDV, newDV)),
        filter(cell => !!cell),
        tap(cell => {
          this.selectActivityWithDrilldown(cell);
          this.removeDrilldownForRetry(cell);
          this.ngZone.run(() => this.openDrilldownModalWithPreset(cell, status));
          cell.comp.instance.setupFromJson();
        })
      )
      .subscribe();
  }

  public cancelGeneration(): Observable<{success: boolean}> {
    const {
      id: dvId,
      map: {id: mapId}
    } = this.diagramJsonSvc.get();

    return this.http.delete<{success: boolean}>(
      `${this.apiUrl}/elements-gpt/${mapId}/map/${dvId}/diagram/generation`
    );
  }

  private removeDrilldownForRetry(cell: MxCell): void {
    this.drilldownSvc.deleteDrilldown(cell);
    this.ngZone.run(() => cell.comp.instance.setupFromJson());
  }

  private openDrilldownModalWithPreset(cell, status: AIDVGenerationStatus): void {
    this.drilldownSvc.openAutoGenerateDrilldownPopup(cell, status.retry.preset);
  }

  private getCell(oldDV, newDV): MxCell {
    const key = oldDV.diagramId;
    const activities = newDV.activities;
    const activity = find(activities, ac => ac.drilldowns && ac.drilldowns[0].targetDbId === key);

    if (!activity) {
      return;
    }

    const mxKey = activity.mxKey;
    const graph = this.editorRegistrySvc.getGraph();

    return graph.model.getCell(Number(mxKey));
  }

  private selectActivityWithDrilldown(cell) {
    const graph = this.editorRegistrySvc.getGraph();

    if (!cell) {
      return;
    }

    const selectionModel = graph.getSelectionModel();

    selectionModel.setCell(cell);
  }
}
